import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AMSER } from "@/data/forge";

export default function Amser() {
  const [picked, setPicked] = useState({});
  const chosen = AMSER.filter((d) => picked[d.key] != null);

  return (
    <section data-testid="amser-section" className="py-12 md:py-16 relative">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(ellipse at 50% 15%, rgba(255,52,52,.06), transparent 60%)" }}
      />
      <div className="relative mx-auto w-[min(1180px,calc(100%-32px))]">
        <div className="text-center mb-10">
          <div className="eyebrow-label mb-4">The language of the app</div>
          <h2 className="headline text-4xl sm:text-5xl lg:text-6xl mb-4 flex justify-center gap-3 sm:gap-5">
            {AMSER.map((die) => (
              <span key={die.key} className="text-white" data-testid={`amser-letter-${die.key}`}>
                {die.key}
              </span>
            ))}
          </h2>
          <p className="text-white/55 text-sm md:text-base max-w-[620px] mx-auto">
            Five dice types that describe any set you can imagine. Tap one face from each and watch
            them assemble into a rolled set.
          </p>
        </div>

        <div className="flex flex-col gap-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 order-2 md:order-1">
          {AMSER.map((die, i) => (
            <motion.div
              key={die.key}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.05 }}
              className="panel rounded-2xl p-5 h-full"
              data-testid={`amser-${die.name.toLowerCase()}`}
            >
              <div className="flex items-center gap-2.5 mb-3">
                <span
                  className="display text-base w-8 h-8 rounded-lg grid place-items-center font-bold"
                  style={{ color: die.color, background: `${die.color}1f`, border: `1px solid ${die.color}55` }}
                >
                  {die.key}
                </span>
                <h3 className="headline text-xl">{die.name}</h3>
              </div>
              <p className="text-[11px] text-white/40 leading-relaxed mb-4 min-h-[40px]">{die.definition}</p>
              <div className="flex flex-wrap gap-1.5">
                {die.faces.map((face) => {
                  const active = picked[die.key] === face;
                  return (
                    <button
                      key={face}
                      data-testid={`amser-face-${die.key}-${face.replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => setPicked((p) => ({ ...p, [die.key]: active ? undefined : face }))}
                      className="text-[10px] px-2.5 py-1 rounded-full border transition-all duration-200 hover:-translate-y-0.5"
                      style={{
                        borderColor: active ? die.color : "rgba(255,255,255,0.12)",
                        background: active ? `${die.color}22` : "rgba(255,255,255,0.03)",
                        color: active ? die.color : "rgba(255,255,255,0.6)",
                      }}
                    >
                      {face}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>

        <div
          data-testid="amser-rolled-set"
          className="order-1 md:order-2 sticky top-[88px] z-30 md:static rounded-2xl border border-forge/30 p-4 md:p-6 text-center shadow-[0_10px_30px_rgba(0,0,0,0.6)]"
          style={{ background: "radial-gradient(circle at 50% 0%, rgba(255,52,52,.12), transparent 65%), #0c0c0e" }}
        >
          {chosen.length === 0 ? (
            <p className="text-white/35 text-sm">Pick a face from each die to forge a set.</p>
          ) : (
            <div className="flex flex-wrap items-center justify-center gap-2.5">
              <AnimatePresence>
                {chosen.map((die) => (
                  <motion.span
                    key={die.key}
                    layout
                    initial={{ opacity: 0, y: 12, rotate: -6 }}
                    animate={{ opacity: 1, y: 0, rotate: 0 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    className="headline text-lg md:text-2xl px-3.5 py-1.5 rounded-xl"
                    style={{ color: die.color, background: `${die.color}14`, border: `1px solid ${die.color}44` }}
                  >
                    {picked[die.key]}
                  </motion.span>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
        </div>
      </div>
    </section>
  );
}
